import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, extend } from 'underscore';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withPreference } from '@bingads-webui-react/preference-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { withScope } from '@bingads-webui-react/scope-service';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withSiteMap } from '@bingads-webui-campaign-react/with-site-map';
import { withCurrentExpandedScope } from '@bingads-webui-campaign-react/use-current-scope-entity';
import { AppContentFooter, getStyles } from '@bingads-webui-react/app-content-footer';
import { isNavigationRefreshOn } from '@bingads-webui-react/preference-utility';


// even if we add React.memo to the functional component, the component will still re-render due to some param change.
// this is to prevent this footer config get computed again
// this assumes that the footer links won't change without full page refresh
let linksGlobal = [];
let locSettingsGlobal = {};

function showImprintUrl(lcid) {
  // show the url for all german languages irrespective of country
  return lcid === 3079 || lcid === 1031 || lcid === 2055;
}

function getSupportUrlConfigKey({ CurrentUser, CurrentCustomer }) {
  let supportUrlConfigKey = 'supporturl_standard';

  // For internal users, don't look at customer service level since the customer service level is undefined
  // Customer service level can change when the internal user impersonate a customer
  // So, always default to standard support URL for internal users
  if (CurrentUser && !CurrentUser.IsInternal) {
    let serviceLevel;

    if (CurrentCustomer) {
      serviceLevel = CurrentCustomer.ServiceLevel;
    }

    if (serviceLevel) {
      switch (serviceLevel) {
        case 'Premium':
        case 'Select':
          supportUrlConfigKey = 'supporturl_premium';
          break;
        case 'Internal':
          supportUrlConfigKey = 'supporturl_internal';
          break;
        default:
          supportUrlConfigKey = 'supporturl_standard';
          break;
      }
    }
  }
  return supportUrlConfigKey;
}

function getLinks({
  serverContext,
  i18n,
  locSettings = {},
  supportUrlConfigKey,
  feedbackEnabled,
  isReadOnlyUser,
}) {
  const linkArr = [];

  if (showImprintUrl(serverContext.CurrentUser.LocaleID) && locSettings.imprintUrl) {
    linkArr.push({
      url: locSettings.imprintUrl,
      text: i18n.getString(_TL_('Imprint')),
    });
  }

  const legalText = i18n.getString(_TL_('Legal'));
  if (serverContext.CurrentUser) {
    linkArr.push({
      url: serverContext.TermsConditionsUrl,
      text: legalText,
    });
  } else if (locSettings.legalurl) {
    linkArr.push({
      url: locSettings.legalurl,
      text: legalText,
    });
  }

  if (locSettings.privacyurl) {
    linkArr.push({
      url: locSettings.privacyurl,
      text: i18n.getString(_TL_('Privacy & Cookies')),
    });
  }

  if (locSettings.advertiseurl) {
    linkArr.push({
      url: locSettings.advertiseurl,
      text: i18n.getString(_TL_('Advertise')),
    });
  }

  if (locSettings.developersurl) {
    linkArr.push({
      url: locSettings.developersurl,
      text: i18n.getString(_TL_('Developers')),
    });
  }

  if (locSettings[supportUrlConfigKey]) {
    linkArr.push({
      url: locSettings[supportUrlConfigKey],
      text: i18n.getString(_TL_('Support')),
    });
  }

  if (locSettings.adcenterCommunityurl) {
    linkArr.push({
      url: locSettings.adcenterCommunityurl,
      text: i18n.getString(_TL_('Blog')),
    });
  }

  if (feedbackEnabled) {
    if (!isReadOnlyUser) {
      linkArr.push({
        url: '',
        text: i18n.getString(_TL_('Feedback')),
        hasFeedbackPanel: feedbackEnabled,
      });
    }

    // if (locSettings.feedback) {
    //   linkArr.push({
    //     url: locSettings.feedback,
    //     text: i18n.getString(_TL_('User Voice')),
    //   });
    // }
  }

  if (locSettings.forumurl) {
    linkArr.push({
      url: locSettings.forumurl,
      text: i18n.getString(_TL_('Community')),
    });
  }

  if (locSettings.consumerhealthprivacyurl) {
    linkArr.push({
      url: locSettings.consumerhealthprivacyurl,
      text: i18n.getString(_TL_('Consumer Health Privacy')),
    });
  }

  return linkArr;
}

export const useFooterConfig = ({
  i18n,
  serverContext,
  feedbackEnabled,
  isReadOnlyUser,
}) => {
  const [links, setLinks] = useState(linksGlobal);
  const [locSettings, setLocSettings] = useState(locSettingsGlobal);
  const supportUrlConfigKey = getSupportUrlConfigKey({
    CurrentUser: serverContext.CurrentUser,
    CurrentCustomer: serverContext.CurrentCustomer,
  });
  useEffect(() => {
    if (isEmpty(links)) {
      window.globalLegacyI18n.loadFooterSettingsI18n().then((footerSettings) => {
        const locSettingsData = footerSettings.data;
        const linkArr = getLinks({
          i18n,
          locSettings: locSettingsData,
          serverContext,
          supportUrlConfigKey,
          feedbackEnabled,
          isReadOnlyUser,
        });

        setLinks(linkArr);
        setLocSettings(locSettingsData);
        linksGlobal = linkArr;
        locSettingsGlobal = locSettingsData;
      });
    }
  // we only read the context and legacy deps once, ignore all the future update.
  // if current user change, we expect the page refresh, so this footer component will mount again automatically.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const footerLinks = useMemo(() => ({
    links,
    locSettings,
    supportUrlConfigKey,
    texts: {
      help: i18n.getString(_TL_('Help')),
      copyright: i18n.getString(_TL_('© {{year}} Microsoft'), { year: new Date().getFullYear() }),
    },
  }), [i18n, links, locSettings, supportUrlConfigKey]);

  return footerLinks;
};

const ContentFooter = React.memo(({
  classes,
  i18n,
  serverContext,
  preferenceService,
  scope,
  permissions,
  siteMap,
  currentCampaign,
  currentAdGroup,
  instrumentation,
}) => {
  const feedbackEnabled = permissions.dynamic.ProductFeedback;
  const { IsReadOnlyUser: isReadOnlyUser } = permissions;

  const {
    CurrentUser,
    Omniture,
    PageTrackingId,
    IsProdEnv,
  } = serverContext;

  const scenario = instrumentation.feedbackScenario && instrumentation.feedbackScenario.create();
  const logger = scenario && scenario.feedbackActivity && scenario.feedbackActivity.create();
  const feedbackLogger = {
    log: (info) => {
      logger.trace(info);
    },
  };
  const feedbackProps = {
    isTestEnv: !IsProdEnv,
    feedbackLogger,
    email: CurrentUser.Email,
    pageId: PageTrackingId,
    sessionId: Omniture && Omniture.SessionId,
  };

  if (isNavigationRefreshOn(permissions)) {
    extend(feedbackProps, { appData: { NavigationRefresh: true } });
  }

  const { links, texts } = useFooterConfig({
    i18n,
    serverContext,
    preferenceService,
    scope,
    permissions,
    siteMap,
    currentCampaign,
    currentAdGroup,
    feedbackEnabled,
    isReadOnlyUser,
  });

  return (
    <AppContentFooter
      links={links}
      texts={texts}
      classes={classes}
      feedbackProps={feedbackProps}
    />
  );
});

/* eslint-disable react/forbid-prop-types */
ContentFooter.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentUser: PropTypes.object.isRequired,
    CurrentCustomer: PropTypes.object.isRequired,
  }).isRequired,
  preferenceService: PropTypes.object.isRequired,
  scope: PropTypes.object.isRequired,
  permissions: PropTypes.object.isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
  currentCampaign: PropTypes.shape({}),
  currentAdGroup: PropTypes.shape({}),
  instrumentation: PropTypes.shape({}).isRequired,
};

ContentFooter.defaultProps = {
  currentCampaign: null,
  currentAdGroup: null,
};

export default withI18nConsumer(withServerContext(withPreference(withPermissions(withInstrumentation(withScope(withCurrentExpandedScope(withSiteMap(withDefaultStyles(
  ContentFooter,
  getStyles
)))))))));
